import React, { useEffect } from 'react';
import { useParams, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetGiftQuery } from '../../../redux/api/authorized/gifts';
import { useGetPublicGiftQuery } from '../../../redux/api/public/gifts';
import { selectPrivyToken } from '../../../redux/slices/auth';
import WidthContainer from '../../../components/WidthContainer';
import styled from 'styled-components';
import ConfirmationSkeleton from '../Confirmation/ConfirmationSkeleton';
import { useGetCurrentUserProfileQuery } from '../../../redux/api/authorized/auth';
import { selectTokenByNetworkAndAddress } from '../../../redux/slices/tokenSlice';
import ClaimGiftSkeleton from './ClaimGiftSkeleton';
import { usePrivy } from '@privy-io/react-auth';
import { useLazySearchTokensQuery } from '../../../redux/api/public/tokens';
import { selectLastUpdated } from '../../../redux/slices/tokenSlice';

const Container = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Message = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
`;

const GiftDetails = ({ currentUser }) => {
  const { id } = useParams();
  const location = useLocation();
  const { ready, authenticated } = usePrivy();
  const privyToken = useSelector(selectPrivyToken);

  const [searchTokens] = useLazySearchTokensQuery();
  const lastUpdated = useSelector(selectLastUpdated);

  const isLoadingUser = privyToken && !currentUser;

  // Use appropriate API based on authentication status
  const shouldUseAuthorizedAPI = id && privyToken && currentUser;
  const shouldUsePublicAPI = id && ready && !authenticated;

  const {
    data: authorizedData,
    error: authorizedError,
    isLoading: isLoadingAuthorized,
  } = useGetGiftQuery(id, {
    skip: !shouldUseAuthorizedAPI,
  });

  const {
    data: publicData,
    error: publicError,
    isLoading: isLoadingPublic,
  } = useGetPublicGiftQuery(id, {
    skip: !shouldUsePublicAPI,
  });

  // Determine which data to use - ensure we only use one source
  const data = shouldUseAuthorizedAPI ? authorizedData : publicData;
  const error = shouldUseAuthorizedAPI ? authorizedError : publicError;
  const isLoading = shouldUseAuthorizedAPI
    ? isLoadingAuthorized
    : isLoadingPublic;

  const gift = data?.gift;
  const baseToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, gift?.network, gift?.baseToken),
  );
  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, gift?.network, gift?.token),
  );

  useEffect(() => {
    if (!token && lastUpdated > 0) {
      searchTokens({
        addresses: [gift?.token],
      });
    }
  }, [lastUpdated, gift?.token, token]);

  if (isLoading || isLoadingUser || !gift || !token) {
    // Render appropriate skeleton based on route
    if (location.pathname.includes('/confirm')) {
      return <ConfirmationSkeleton />;
    } else if (location.pathname.includes('/claim')) {
      return <ClaimGiftSkeleton />;
    }
    return <ClaimGiftSkeleton />; // default skeleton
  }

  if (error) {
    return (
      <WidthContainer>
        <Container>
          <Message>Error loading gift</Message>
        </Container>
      </WidthContainer>
    );
  }

  const isCreator = currentUser?.id === gift.creatorId;
  const isReceiver = currentUser?.id === gift.receiverId;

  const contextValue = {
    gift,
    token,
    baseToken,
    isCreator,
    isReceiver,
  };

  return <Outlet context={contextValue} />;
};

export default GiftDetails;
